"use client";

import "keen-slider/keen-slider.min.css";
import React, { useEffect, useRef, useState, useCallback } from "react";
import KeenSlider from "keen-slider";
import CustomImage from "components/atoms/Image/CustomImage";
import { AdvertisementResponse } from "types/Advertisements";
import { useRouter } from "next/router";
import { useLang } from "hooks/useLang";
import useWindowSize from "hooks/useWindowSize";

/* =======================
   CONSTANTS
======================= */

const AUTO_SCROLL_DELAY = 5000;

/* =======================
   TYPES
======================= */

interface Props {
  ads: AdvertisementResponse["payload"];
}

/* =======================
   COMPONENT
======================= */

const HeroAdsStandalone = ({ ads }: Props) => {
  const sliderRef = useRef<HTMLDivElement | null>(null);
  const sliderInstance = useRef<any>(null);

  const [currentSlide, setCurrentSlide] = useState(0);
  const [loaded, setLoaded] = useState(false);

  const router = useRouter();
  const { lang } = useLang();
  const { isMobile } = useWindowSize();

  const isArabic = lang === "ar";

  /* =======================
     FILTER ADS
  ======================= */

  const sliderAds = ads?.filter((ad) => ad.web_view_type === "slider");
  const staticAds = ads?.filter((ad) => ad.web_view_type === "static");

  /* =======================
     SLIDER INIT
  ======================= */

  useEffect(() => {
    if (!sliderRef.current || sliderInstance.current || !sliderAds?.length)
      return;

    sliderInstance.current = new KeenSlider(
      sliderRef.current,
      {
        loop: true,
        rtl: isArabic,
        slideChanged(slider) {
          setCurrentSlide(slider.track.details.rel);
        },
        created() {
          setLoaded(true);
        },
      },
      [
        (slider) => {
          let timeout: any;

          const clear = () => clearTimeout(timeout);
          const next = () => {
            clear();
            timeout = setTimeout(() => slider.next(), AUTO_SCROLL_DELAY);
          };

          slider.on("created", next);
          slider.on("dragStarted", clear);
          slider.on("animationEnded", next);
          slider.on("updated", next);
        },
      ]
    );

    return () => {
      sliderInstance.current?.destroy();
      sliderInstance.current = null;
    };
  }, [sliderAds?.length, isArabic]);

  /* =======================
     DOT CLICK
  ======================= */

  const handleDotClick = useCallback((idx: number) => {
    sliderInstance.current?.moveToIdx(idx);
  }, []);

  if (!sliderAds?.length && !staticAds?.length) return null;

  return (
    <div
      className="layout md:max-w-[1220px] w-full   relative"
      dir={isArabic ? "rtl" : "ltr"}
    >
      {/* Animations */}
      <style>{`
        @keyframes slideInFade {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .slider-enter {
          animation: slideInFade 0.6s ease-out forwards;
        }

        .dot-active {
          transform: scale(1.2);
        }
      `}</style>

      {/* =======================
          SLIDER
      ======================= */}

      {sliderAds?.length > 0 && (
        <>
          <div
            ref={sliderRef}
            className="keen-slider lg:aspect-[16/8] mt-[50px] md:mt-[100px] aspect-video overflow-hidden rounded-xl w-full slider-enter md:h-[500px]"
          >
            {sliderAds.map((img, idx) => (
              <div
                key={img._id}
                className={`keen-slider__slide relative w-full h-full ${
                  img.type === "single" ? "cursor-pointer" : ""
                }`}
                onClick={() => {
                  if (img.type === "single") {
                    router.push(`/events/${img.events[0]}`);
                  }
                }}
              >
                <CustomImage
                  src={`${process.env.NEXT_PUBLIC_Event_Img_URL}${
                    isMobile ? img.image : img.web_image
                  }`}
                  alt="Advertisement"
                  fill
                  priority={idx === 0}
                  className="object-cover transition-transform duration-500 hover:scale-105"
                />
              </div>
            ))}
          </div>

          {/* Dots */}
          {loaded && sliderAds.length > 1 && (
            <div
              className={`absolute bottom-[40px] ${
                isArabic ? "right-[40px]" : "left-[40px]"
              } flex gap-2 z-20`}
            >
              {sliderAds.map((_, idx) => (
                <span
                  key={idx}
                  onClick={() => handleDotClick(idx)}
                  className={`h-3 w-3 rounded-full cursor-pointer transition-all ${
                    currentSlide === idx
                      ? "bg-[#ED1A5A] dot-active"
                      : "bg-white/80 hover:scale-110"
                  }`}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* =======================
          STATIC IMAGES
      ======================= */}

      {staticAds?.length > 0 && (
        <div className="grid md:grid-cols-2 gap-6 mt-6">
          {staticAds.map((img) => (
            <div
              key={img._id}
              className="relative aspect-[16/9] rounded-xl overflow-hidden cursor-pointer group"
              onClick={() => {
                if (img.type === "single") {
                  router.push(`/events/${img.events[0]}`);
                }
              }}
            >
              <CustomImage
                src={`${process.env.NEXT_PUBLIC_Event_Img_URL}${
                  isMobile ? img.image : img.web_image
                }`}
                alt="Advertisement"
                fill
                className="object-cover transition-transform duration-500 group-hover:scale-105"
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default HeroAdsStandalone;
